import React from "react";
import ReactDOM from "react-dom";

import { Keyboard } from "@capacitor/keyboard";
import { undo } from "prosemirror-history";
import { Node as ProseNode } from "prosemirror-model";
import { NodeSelection, Plugin, TextSelection } from "prosemirror-state";
import uuid4 from "uuid/v4";

import { type MenuEntry, MenuExtension } from "~/lib/prose/extension";
import { choiceToTypeaheadResult } from "~/lib/prose/plugins/slash";
import type { TypeAheadResult } from "~/lib/prose/plugins/typeahead";
import { isNative } from "~/lib/utils";

import PinnedToolbar from "./PinnedToolbar";

import styles from "./PinnedToolbar.module.css";

export class PinnedFormattingTools extends MenuExtension {
	toolbarDOM: HTMLElement;
	attachmentsContainer?: HTMLElement;

	get name() {
		return "pinned-formatting-tools";
	}

	constructor(
		options: {
			setAttachmentsRef?: (el: HTMLElement) => void;
			noJira?: boolean;
			noMention?: boolean;
			isExternalUser?: boolean;
			excludeChoice?: (choice: ReturnType<typeof choiceToTypeaheadResult>) => boolean;
			compact?: boolean;
			mountPointId?: string;
			className?: string;
			postProcessEntries?: (entries: MenuEntry[]) => MenuEntry[];
			onChoiceClickOverride?: (choice: TypeAheadResult, next: () => void) => void;
		} = {},
	) {
		super(options);
		this.toolbarDOM = document.createElement("div");
		this.toolbarDOM.classList.add(styles.toolbarWrapper);
		document.body.appendChild(this.toolbarDOM);

		if (options.setAttachmentsRef) {
			this.attachmentsContainer = document.createElement("div");
		}
	}

	get plugins() {
		const extension = this;
		return [
			new Plugin({
				view: () => {
					return {
						update: view => {
							if (view.editable) {
								extension.renderToolbar();
							} else {
								extension.hideToolbar();
							}
						},
					};
				},

				appendTransaction(trs, _oldState, newState) {
					if (trs.some(tr => (tr.docChanged || tr.selectionSet) && !tr.getMeta("remoteChange"))) {
						return newState.tr.scrollIntoView();
					}

					return undefined;
				},
			}),
		];
	}

	init() {
		if (window.visualViewport) {
			window.visualViewport.addEventListener("resize", this.repositionToolbar);
			window.visualViewport.addEventListener("scroll", this.repositionToolbar);
		}

		if (isNative) {
			Keyboard.addListener("keyboardWillShow", this.renderToolbar);
			Keyboard.addListener("keyboardWillHide", this.hideToolbar);
		}

		if (this.attachmentsContainer && this.options.setAttachmentsRef) {
			if (!this.editorView?.dom.parentNode) return;

			this.editorView.dom.parentNode.appendChild(this.attachmentsContainer);
			this.options.setAttachmentsRef(this.attachmentsContainer);
		}
	}

	destroy = () => {
		ReactDOM.unmountComponentAtNode(this.toolbarDOM);
		this.toolbarDOM.remove();

		if (window.visualViewport) {
			window.visualViewport.removeEventListener("resize", this.repositionToolbar);
			window.visualViewport.removeEventListener("scroll", this.repositionToolbar);
		}

		if (isNative) {
			Keyboard.removeAllListeners();
		}

		if (this.attachmentsContainer && this.options.setAttachmentsRef) {
			this.attachmentsContainer.remove();
		}
	};

	repositionToolbar = () => {
		requestAnimationFrame(() => {
			const viewport = window.visualViewport;
			if (!viewport) return;

			const offsetX = viewport.offsetLeft;
			const offsetY = viewport.height - window.document.body.getBoundingClientRect().height + viewport.offsetTop;
			const scale = 1 / viewport.scale;

			this.toolbarDOM.style.transform = `translate3d(${offsetX}px, ${offsetY}px, 0) scale(${scale})`;

			const heightDifference = window.innerHeight - viewport.height;

			// Keyboard is considered open if the height difference is significant -Cursor
			if (heightDifference > 150) {
				this.renderToolbar();
			} else {
				this.hideToolbar();
			}
		});
	};

	closeKeyboard = () => {
		if (document.activeElement instanceof HTMLElement) {
			document.activeElement.blur();
		}

		if (isNative) {
			Keyboard.hide();
		}
	};

	undo = () => {
		if (!this.editorView) return;
		this.editorView.focus();

		const { state, dispatch } = this.editorView;
		undo(state, dispatch, this.editorView);
	};

	renderToolbar = () => {
		if (!this.editorView?.hasFocus()) return;
		this.toolbarDOM.classList.remove(styles.isHidden);

		ReactDOM.render(
			<PinnedToolbar
				extension={this}
				editorView={this.editorView}
				closeKeyboard={this.closeKeyboard}
				entries={this.allEntries}
				addBlock={this.addBlock}
				undo={this.undo}
			/>,
			this.toolbarDOM,
		);
	};

	hideToolbar() {
		this.toolbarDOM.classList.add(styles.isHidden);
	}

	private addBlock = (menuEntry: MenuEntry) => {
		if (!this.editorView) return;
		this.editorView.focus();
		const { state, dispatch } = this.editorView;

		if (menuEntry.group === "insert") {
			menuEntry.run()(state, tr => (dispatch(tr.scrollIntoView()), true), this.editorView);
			return;
		}

		const { $from } = state.selection;
		const { indent } = $from.node().attrs;
		const attrs: { blockId: string; indent?: number } = { blockId: uuid4() };
		if (indent !== undefined) attrs.indent = indent;
		const newBlock = state.schema.nodes.unstyled.createAndFill(attrs);
		if (!newBlock) {
			console.error("Failed to create a new block");
			return;
		}

		let { tr } = state;

		let selectedBlock = $from.node(1) as ProseNode | undefined | null;
		if (state.selection instanceof NodeSelection) {
			selectedBlock = state.selection.$from.nodeAfter;
		}
		let pos = 0;

		for (let i = 0; i < tr.doc.childCount; i += 1) {
			const child = tr.doc.child(i);
			pos += child.nodeSize;
			if (selectedBlock && child.attrs.blockId === selectedBlock.attrs.blockId) break;
		}

		tr = tr.insert(pos, newBlock);

		tr = tr.setSelection(new TextSelection(tr.doc.resolve(pos + 1)));
		dispatch(tr);

		menuEntry.run()(this.editorView.state, tr => (dispatch(tr.scrollIntoView()), true), this.editorView);
	};
}

export default PinnedFormattingTools;
